/**
 * 轻量博客加载器
 *
 * 发布流程：往 posts/ 目录丢一个 .md 文件 → npm run build → 部署。完毕。
 *
 * 文件名即 URL：posts/hello-world.md → /blog/hello-world
 * frontmatter 只需 title + date，description/tags 可选：
 *
 * ---
 * title: 文章标题
 * date: 2026-09-08
 * description: 一句话摘要（列表页和 SEO 用）
 * ---
 */

export interface BlogPostMeta {
  /** URL slug，来自文件名（去 .md） */
  slug: string;
  title: string;
  date: string;
  description?: string;
  /** markdown 正文（frontmatter 已剥离） */
  content: string;
}

interface Frontmatter {
  title?: string;
  date?: string;
  description?: string;
}

/** 解析 --- 包围的简单 frontmatter（只支持 key: value 一行一个） */
function parseFrontmatter(raw: string): { fm: Frontmatter; body: string } {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/);
  if (!match) return { fm: {}, body: raw };

  const fm: Frontmatter = {};
  for (const line of match[1].split('\n')) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
    if (key === 'title' || key === 'date' || key === 'description') {
      fm[key] = value;
    }
  }
  return { fm, body: match[2] };
}

// 构建时静态收集所有文章，无运行时请求
const modules = import.meta.glob('./posts/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function loadAll(): BlogPostMeta[] {
  const posts: BlogPostMeta[] = [];
  for (const [filePath, raw] of Object.entries(modules)) {
    const slug = filePath.split('/').pop()!.replace(/\.md$/, '');
    const { fm, body } = parseFrontmatter(raw);
    posts.push({
      slug,
      title: fm.title || slug,
      date: fm.date || '1970-01-01',
      description: fm.description,
      content: body,
    });
  }
  // 日期倒序，最新在前
  return posts.sort((a, b) => b.date.localeCompare(a.date));
}

const ALL_POSTS = loadAll();

export function getAllPosts(): BlogPostMeta[] {
  return ALL_POSTS;
}

export function getPost(slug: string): BlogPostMeta | undefined {
  return ALL_POSTS.find((p) => p.slug === slug);
}
